import { cloneDeep, merge, set } from 'lodash'
import FormModal from 'js/components/form-modal'
import openModalConfirm from './_confirm'
import { openModalAlert } from 'js/services/modals'
import validatePresence from './_validate'
import template from './decision.pug'

const DefaultData = () => ({
  name: '',
  config: {
    conditions: [],
    defaultNextStepId: null
  },
  branchs: [],
  dinamicBranches: 0
})

export default Vue.extend({
  template: template(),
  components: {
    FormModal
  },
  props: {
    node: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      data: merge(DefaultData(), cloneDeep(this.node)),
      localConditions: []
    }
  },
  computed: {
    // Cargamos las etiquetas dinámicas de forma segura y ordenada
    tagOptions() {
      if (!this.node || !this.node.tags || !Array.isArray(this.node.tags)) return [];
      
      return this.node.tags
        .filter(item => item != null && typeof item === 'object')
        .map(item => ({
          label: item.name || item.key || 'Sin nombre',
          value: item.type && item.key ? `${item.type}.${item.key}` : item.key
        }))
        .sort((a, b) => {
          const labelA = String(a.label || '');
          const labelB = String(b.label || '');
          return labelA.localeCompare(labelB);
        });
    },

    errors() {
      return merge(DefaultData(), this.data.errors)
    },
    
    conditionsErrors() {
      return this.localConditions.filter(item => item.errors).length
    },

    operatorTextOptions() {
      return [
        COMPARISON_OPERATORS.EQUAL,
        COMPARISON_OPERATORS.DISTINCT,
        COMPARISON_OPERATORS.CONTAINS,
        COMPARISON_OPERATORS.GREATER_THAN,
        COMPARISON_OPERATORS.LESS_THAN,
        COMPARISON_OPERATORS.STAYIN
      ].map(item => ({label: this.$t(`comparisonOperators.text.${item}`), value: item}))
    },
    operatorNumberOptions() {
      return [
        COMPARISON_OPERATORS.EQUAL,
        COMPARISON_OPERATORS.LESS_THAN,
        COMPARISON_OPERATORS.GREATER_THAN
      ].map(item => ({label: this.$t(`comparisonOperators.number.${item}`), value: item}))
    }
  },
  created() {
    // Si el nodo ya trae condiciones guardadas, las mapeamos al array local
    if (this.data.config.conditions && this.data.config.conditions.length) {
      this.data.config.conditions.forEach(condition => {
        this.localConditions.push({
          field: condition.field || '',
          operator: condition.operator || '',
          value: condition.value || '',
          nextStepId: condition.nextStepId || '',
          errors: null
        })
      })
    } else {
      // Si es un nodo nuevo, agregamos la primera condición por defecto
      this.addCondition()
    }

    if (this.node.config) this.validateFields()
  },
  methods: {
    t(key, options = {}) {
      return this.$t(`campaigns.show.automations.workflow.nodes.decision.${key}`, options)
    },

    addCondition() {
      this.localConditions.push({
        field: '',
        operator: '',
        value: '',
        nextStepId: '',
        errors: null
      })
    },
    isTagNumeric(field) {
      if (!field) return false

      const fieldParts = field.split('.');
      const tagType = fieldParts[0];
      const tagKey = fieldParts[1];

      const tag = (this.node.tags || []).find(item => item.key === tagKey && item.type === tagType);

      //Se verifica si existe el tag y si su tipo es numerico
      return tag && tag.fieldType === FIELD_TYPES.NUMERIC;
    },

    getOperatorOptions(field) {
      return this.isTagNumeric(field) ? this.operatorNumberOptions : this.operatorTextOptions
    },

    canDeleteCondition(index) {
      try {
        const nextStepId = this.localConditions[index].nextStepId;
        if (!nextStepId) return true; 

        let parentComponent = this.$parent;
        while (parentComponent && !parentComponent.nodes) {
          parentComponent = parentComponent.$parent;
        }

        if (parentComponent && parentComponent.nodes) {
          const childNode = parentComponent.nodes.find(n => n.id === nextStepId);
          if (childNode && !childNode.dropzone) {
            return false; 
          }
        }
        return true;
      } catch (error) {
        console.error("Error al validar eliminación:", error);
        return true; 
      }
    },

    deleteCondition(index) {
      if (!this.canDeleteCondition(index)) {
        openModalAlert(
          this.t('alertModal.deleteBlocked.title'),
          this.t('alertModal.deleteBlocked.body'),
          this.t('alertModal.deleteBlocked.okButton')
        );
        return;
      }

      const nextStepId = this.localConditions[index].nextStepId;

      if (nextStepId) {
        try {
          let parentComponent = this.$parent;
          while (parentComponent && !parentComponent.nodes) {
            parentComponent = parentComponent.$parent;
          }
          
          if (parentComponent && parentComponent.nodes) {
            const dropIndex = parentComponent.nodes.findIndex(n => n.id === nextStepId);
            if (dropIndex > -1) {
              parentComponent.nodes.splice(dropIndex, 1);
            }
          }

          if (this.data.branchs) {
            const branchIndex = this.data.branchs.findIndex(b => b.nextStepId === nextStepId);
            if (branchIndex > -1) {
              this.data.branchs.splice(branchIndex, 1);
            }
          }
        } catch (e) {
          console.error("Error limpiando el lienzo visual: ", e);
        }
      }

      this.localConditions.splice(index, 1);
    },

    save() {
      this.data.config.conditions = this.localConditions.map(cond => ({
        field: cond.field,
        operator: cond.operator,
        value: cond.value,
        nextStepId: cond.nextStepId
      }))

      validatePresence(this.data, ['name'])
      this.validateFields()

      if (this.data.errors || this.conditionsErrors) {
        openModalConfirm().then(() => {
          this.data.dinamicBranches = this.localConditions.length
          this.data.stepType = 'switch'
          this.$emit('save', cloneDeep(this.data))
        })
      } else {
        this.data.dinamicBranches = this.localConditions.length
        this.data.stepType = 'switch'
        this.$emit('save', cloneDeep(this.data))
      }
    },

    validateFields() {
      let hasError = false

      if (this.localConditions.length === 0) {
        set(this.data, 'errors.config.conditions', this.t('errors.minimumConditions'))
        hasError = true
      }

      this.localConditions.forEach(cond => {
        cond.errors = null
        validatePresence(cond, ['field', 'operator', 'value'])
        
        if (cond.errors) hasError = true
      })

      if (hasError) set(this.data, 'errors.config.conditionsError', true)
    }
  }
})